import React from 'react';
import { useIntl, defineMessage } from 'react-intl';
import { Table } from 'reactstrap';
import { faUsers } from '@fortawesome/pro-light-svg-icons/faUsers';
import { TypedMemo } from '../../../common/types';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { SMLLink } from '../SMLLink/SMLLink';
import { PercentageChangeElement } from '../PositionChangeElement';
import { LoadingView } from '../../../common/views/LoadingView';
import { SMLImage } from '../SMLImage';
import { SMLEmptyState } from '../SMLEmptyState/SMLEmptyState';
import { useArtistStats } from '../../hooks/useArtistStats';
import { MiniScoreChart } from '../MiniScoreChart/MiniScoreChart';
import styles from './ArtistsTable.module.scss';

// Basic artist data returned by listFollowed endpoint
export interface ArtistBasicData {
  id: string;
  name: string;
  imageUrl?: string | null;
}

// Full artist data with stats (for backwards compatibility)
export interface ArtistTableData extends ArtistBasicData {
  addedToPlaylists: number;
  addedToPlaylistsChange: number | null;
  removedFromPlaylists: number;
  removedFromPlaylistsChange: number | null;
  appearedInPlaylists: number;
  appearedInPlaylistsChange: number | null;
}

export interface ArtistsTableProps {
  artists: ArtistBasicData[];
  isLoading?: boolean;
  selectedDateValue?: string;
  selectedDate?: Date;
}

const messages = {
  artistName: defineMessage({
    id: 'app.sml.artists.table.artistName',
    defaultMessage: 'Artist Name',
  }),
  playlistGainScore: defineMessage({
    id: 'app.sml.artists.table.playlistGainScore',
    defaultMessage: 'Playlist Gain Score',
  }),
  playlistLossScore: defineMessage({
    id: 'app.sml.artists.table.playlistLossScore',
    defaultMessage: 'Playlist Loss Score',
  }),
  playlistVisibilityScore: defineMessage({
    id: 'app.sml.artists.table.playlistVisibilityScore',
    defaultMessage: 'Playlist Visibility Score',
  }),
  trend: defineMessage({
    id: 'app.sml.artists.table.trend',
    defaultMessage: 'Trend',
  }),
  actions: defineMessage({
    id: 'app.sml.artists.table.actions',
    defaultMessage: 'Actions',
  }),
  viewDetails: defineMessage({
    id: 'app.sml.artists.table.viewDetails',
    defaultMessage: 'View Details',
  }),
};

export const ARTISTS_EXPORT_FILENAME = 'artists';

const formatNumber = (value: number): string => {
  return value.toLocaleString('en-US');
};

const renderPlaceholderImage = (): React.ReactNode => (
  <div className={styles.placeholderImage}>
    <div className={styles.placeholderCircle} />
  </div>
);

// Skeleton loader for stats cells
const StatsLoadingSkeleton: React.FC = () => (
  <div className={styles.metricContainer}>
    <span className={styles.metricSkeleton} />
  </div>
);

// Individual artist row component that fetches its own stats
interface ArtistRowProps {
  artist: ArtistBasicData;
  selectedDateValue?: string;
  selectedDate?: Date;
}

const ArtistRowComponent: React.FC<ArtistRowProps> = ({ artist, selectedDateValue, selectedDate }) => {
  const intl = useIntl();
  const { additionsStats, appearancesStats, isAdditionsLoading, isAppearancesLoading } = useArtistStats(
    artist.id,
    selectedDate
  );

  return (
    <tr>
      <td className={styles.dataCell}>
        <div className={styles.artistInfo}>
          {artist.imageUrl ? (
            <SMLImage src={artist.imageUrl} alt={artist.name} width={24} makeSquareFit className={styles.artistImage} />
          ) : (
            renderPlaceholderImage()
          )}
          <SMLLink
            to={SMLRoutes.artistDetails}
            params={{ artistId: artist.id, ...(selectedDateValue !== undefined && { date: selectedDateValue }) }}
            className={styles.artistName}
          >
            {artist.name}
          </SMLLink>
        </div>
      </td>

      <td className={styles.dataCell}>
        {isAdditionsLoading || !additionsStats ? (
          <StatsLoadingSkeleton />
        ) : (
          <div className={styles.metricContainer}>
            <span className={styles.metricValue}>{formatNumber(additionsStats.addedToPlaylists)}</span>
            <PercentageChangeElement percentageChange={additionsStats.addedToPlaylistsChange} />
          </div>
        )}
      </td>

      <td className={styles.dataCell}>
        {isAdditionsLoading || !additionsStats ? (
          <StatsLoadingSkeleton />
        ) : (
          <div className={styles.metricContainer}>
            <span className={styles.metricValue}>{formatNumber(additionsStats.removedFromPlaylists)}</span>
            <PercentageChangeElement percentageChange={additionsStats.removedFromPlaylistsChange} invertColorLogic />
          </div>
        )}
      </td>

      <td className={styles.dataCell}>
        {isAppearancesLoading || !appearancesStats ? (
          <StatsLoadingSkeleton />
        ) : (
          <div className={styles.metricContainer}>
            <span className={styles.metricValue}>{formatNumber(appearancesStats.appearedInPlaylists)}</span>
            <PercentageChangeElement percentageChange={appearancesStats.appearedInPlaylistsChange} />
          </div>
        )}
      </td>

      <td className={styles.dataCell}>
        <MiniScoreChart entityType="artist" entityId={artist.id} date={selectedDate} />
      </td>

      <td className={styles.dataCellAction}>
        <SMLLink
          to={SMLRoutes.artistDetails}
          params={{ artistId: artist.id, ...(selectedDateValue !== undefined && { date: selectedDateValue }) }}
          className="btn btn-primary btn-sm"
        >
          {intl.formatMessage(messages.viewDetails)}
        </SMLLink>
      </td>
    </tr>
  );
};

const ArtistRow = TypedMemo(ArtistRowComponent);

const ArtistsTableComponent: React.FC<ArtistsTableProps> = ({
  artists,
  isLoading = false,
  selectedDateValue,
  selectedDate,
}) => {
  const intl = useIntl();

  if (isLoading) {
    return (
      <div className={styles.tableContainer}>
        <LoadingView />
      </div>
    );
  }

  if (artists.length === 0) {
    return (
      <div className={styles.tableContainer}>
        <SMLEmptyState
          icon={faUsers}
          title={intl.formatMessage({
            id: 'app.sml.artists.table.empty.title',
            defaultMessage: 'No artists yet',
          })}
          description={intl.formatMessage({
            id: 'app.sml.artists.table.empty.description',
            defaultMessage: 'Follow some artists to see their playlist metrics here.',
          })}
        />
      </div>
    );
  }

  return (
    <div className={styles.tableContainer}>
      <Table responsive hover className={styles.table}>
        <thead>
          <tr>
            <th className={styles.headerCell}>{intl.formatMessage(messages.artistName)}</th>
            <th className={styles.headerCell}>{intl.formatMessage(messages.playlistGainScore)}</th>
            <th className={styles.headerCell}>{intl.formatMessage(messages.playlistLossScore)}</th>
            <th className={styles.headerCell}>{intl.formatMessage(messages.playlistVisibilityScore)}</th>
            <th className={styles.headerCell}>{intl.formatMessage(messages.trend)}</th>
            <th className={styles.headerCellAction}>{intl.formatMessage(messages.actions)}</th>
          </tr>
        </thead>
        <tbody>
          {artists.map((artist) => (
            <ArtistRow
              key={artist.id}
              artist={artist}
              selectedDateValue={selectedDateValue}
              selectedDate={selectedDate}
            />
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export const ArtistsTable = TypedMemo(ArtistsTableComponent);
